import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { CreateTransitAddressDTO, useGetCoinsByTypeQuery } from '../../../../api';

type CreateAddressModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onCancel: () => void;
  isLoading: boolean;
  fields: CreateTransitAddressDTO['body'] | null;
  fieldsHandler: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  submitDisabling: boolean;
};

export const CreateAddressModal: FC<CreateAddressModalProps> = (props) => {
  const { open, onClose, onSuccess, onCancel, isLoading, fields, fieldsHandler, submitDisabling } =
    props;
  const { t } = useTranslation();

  // api
  const { data: coins, isLoading: isCoinsLoading } = useGetCoinsByTypeQuery({ type: 'crypto' });

  // elements
  const coinsOptions = coins?.coins.map((coin) => {
    return (
      <MenuItem key={coin.id} value={coin.id}>
        {coin.name}
      </MenuItem>
    );
  });

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { minWidth: '380px' } }}>
      <DialogTitle>{t('transit-addresses.add-address-modal.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            select
            name="coin_id"
            value={fields?.coin_id ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('transit-addresses.add-address-modal.fields.coin-id')}
            fullWidth
          >
            {coinsOptions}
          </TextField>

          <TextField
            name="address"
            value={fields?.address ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('transit-addresses.add-address-modal.fields.blockchain-address')}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onSuccess()}
          disabled={submitDisabling || isLoading || isCoinsLoading}
        >
          {t('transit-addresses.add-address-modal.create-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onCancel()}>
          {t('transit-addresses.add-address-modal.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
